// Write-ahead log. Every put/delete is serialized to bytes and persisted
// BEFORE the memtable is touched — a write is acknowledged only after the
// WAL record is durable in the store. Recovery replays records in sequence
// order. After a flush makes a prefix durable in an SSTable, that prefix is
// truncated from the log.
//
// Record layout: u8 op (0=put, 1=delete) | f64 seq | u16 keyLen | key utf8
//                | u32 valLen | value utf8

import { KVStore } from "./store";
import { Entry } from "./types";

const WAL_TABLE = "wal";
const enc = new TextEncoder();
const dec = new TextDecoder();

/** Fixed-width so string-ordered store keys match numeric seq order. */
function seqKey(seq: number): string {
  return String(seq).padStart(16, "0");
}

export class Wal {
  constructor(private readonly store: KVStore) {}

  /**
   * Serialize one record and persist it. Resolves only once the store has
   * accepted the bytes — the caller must not acknowledge the write before
   * this resolves, which is the whole durability contract.
   */
  async append(e: Entry): Promise<{ bytes: number; logKey: string }> {
    const kb = enc.encode(e.key);
    const vb = e.value === null ? new Uint8Array(0) : enc.encode(e.value);
    const buf = new Uint8Array(1 + 8 + 2 + kb.length + 4 + vb.length);
    const dv = new DataView(buf.buffer);
    dv.setUint8(0, e.value === null ? 1 : 0);
    dv.setFloat64(1, e.seq, true);
    dv.setUint16(9, kb.length, true);
    buf.set(kb, 11);
    dv.setUint32(11 + kb.length, vb.length, true);
    buf.set(vb, 15 + kb.length);
    const logKey = seqKey(e.seq);
    await this.store.put(WAL_TABLE, logKey, buf.buffer);
    return { bytes: buf.byteLength, logKey };
  }

  /** All records in seq order — recovery replays these into a fresh memtable. */
  async replay(): Promise<Entry[]> {
    const rows = await this.store.entries(WAL_TABLE);
    const out: Entry[] = [];
    for (const [, v] of rows) {
      const buf = new Uint8Array(v as ArrayBuffer);
      const dv = new DataView(buf.buffer);
      const op = dv.getUint8(0);
      const seq = dv.getFloat64(1, true);
      const klen = dv.getUint16(9, true);
      const key = dec.decode(buf.subarray(11, 11 + klen));
      const vlen = dv.getUint32(11 + klen, true);
      const value =
        op === 1
          ? null
          : dec.decode(buf.subarray(15 + klen, 15 + klen + vlen));
      out.push({ key, value, seq });
    }
    out.sort((a, b) => a.seq - b.seq);
    return out;
  }

  /** Drop records with seq <= upTo (their data now lives in an SSTable). */
  async truncate(upTo: number): Promise<void> {
    const rows = await this.store.entries(WAL_TABLE);
    for (const [k] of rows) {
      if (k <= seqKey(upTo)) await this.store.delete(WAL_TABLE, k);
    }
  }

  async recordCount(): Promise<number> {
    return (await this.store.entries(WAL_TABLE)).length;
  }
}
